import React, { useEffect, useRef, useState } from 'react'
import WebComponentContainer from '../generic/WebComponentContainer'
import { BrowserUtils, StringUtils } from '../../utils/siteUtils'
import useEventBus from '../generic/useEventBus'
import { VehiclePageNumber } from '../../constants'
import { mergeWith } from 'lodash'
import { DEFAULT_INSTALLATION_MODE, LandingType } from '../../utils/landingUtils'

export default props => {
  const {
    landingType = 'unassigned',
    offerCode = '',
    dealerId = null,
    isCombined = false,
    environment,
    experienceState,
    updateGlobalState,
    location,
    submitVinNoForm,
    submitPlateStateForm,
    resetPlateStateVinForm,
    isReviewResubmitOffer,
    offer,
    navigateToNewOfferWithVin,
    navigatePageWithState,
    vinModalInfo,
    duplicateInfo,
    navigateWithState
  } = props
  const [pageLoaded, setPageLoaded] = useState(false)
  const [showLoader, setShowLoader] = useState(true)
  const [vehicleData, setVehicleData] = useState()
  const [modalInfo, setModalInfo] = useState('')
  const [dynamicEntryWorkflow, setDynamicEntryWorkflow] = useState(LandingType.Unassigned)
  const unassignedTabList = landingType === LandingType.Unassigned && offerCode && (offerCode.toUpperCase() === 'OC' || offerCode.toUpperCase() === 'TC') ? ['vin', 'plate', 'ymm'] : ['vin', 'plate']
  const showLandingContact = ['atc', 'kbb', 'kbbBranding'].includes(experienceState.headerType)
  const showCityscape = experienceState.headerType !== 'atc'
  const category = experienceState.isAdmin ? 'dat' : experienceState.experience
  const cobrandRequireVinPlaceTabList = experienceState.isRequireVinPlate ? ['vin', 'plate'] : ['vin', 'plate', 'ymm']
  const landingTabList = ['vin', 'plate', 'ymm']
  const tabListUnAssign = experienceState.isCobrand ? JSON.stringify(cobrandRequireVinPlaceTabList) : JSON.stringify(unassignedTabList)
  const tabListAssign = experienceState.isCobrand ? JSON.stringify(cobrandRequireVinPlaceTabList) : JSON.stringify(landingTabList)
  const isCobranding = !!(location?.initQueryParams?.groupid || experienceState.cobrandingInfo)
  const cobrandingOEMImage = experienceState.cobrandingInfo?.imageUrl
  const hydratorRef = useRef(null)

  useEventBus('ReceivedParams', ({ dynamicEntryWorkflow }) => {
    if (hydratorRef?.current) {
      hydratorRef.current.tabList = cobrandRequireVinPlaceTabList
    }

    if (dynamicEntryWorkflow) {
      setShowLoader(false)
    }
  })

  useEventBus('FindByVin', ({ vin }) => {
    if (vin) {
      updateGlobalState({ vin })
      submitVinNoForm(vin)
      updateGlobalState({ entry: 'vin' })
    }
  })

  useEventBus('FindByPlate', ({ licensePlateNumber, licensePlateState }) => {
    submitPlateStateForm({ plate: licensePlateNumber, state: licensePlateState })
    updateGlobalState({ entry: 'lp' })
  })

  useEventBus('FindByYmm', () => {
    updateGlobalState({ entry: 'mm' })
  })

  useEventBus('FindByYmmSuccess', () => {
    resetPlateStateVinForm(2)
  })

  useEventBus('FindByPlateSuccess', ({ isEligible, hasError }) => {
    if (!hasError) {
      updateGlobalState({ fireAnalytics: 'cta::start-my-offer-success::lp' })
    }
  })

  useEventBus('ChangeDifferentVehicleSuccess', () => {
    const vehicleData = { t: '', e: '', d: '', c: '' }
    navigateWithState(vehicleData)
  })

  useEventBus('ProceedWithoutICO', ({ reason, year, make, model, trim, vin }) => {
    fireTradeFailed(reason, year, make, model, trim, vin || '')
  })

  useEventBus('LandingNextClick', ({ vin, mileage, trimId, yearId, make, model, engineId, drivetrainId, transmissionId, genericOptions, autoCheckData }) => {
    if (experienceState.isAdmin) {
      navigatePageWithState({ m: parseInt(mileage), s: trimId, p: VehiclePageNumber, lp: VehiclePageNumber })
      return
    }

    let urlState = StringUtils.getUrlState(location) || {}
    urlState = {
      ...urlState,
      p: VehiclePageNumber,
      lp: VehiclePageNumber,
      s: trimId,
      m: mileage
    }

    let stateForVinAndLP = {
      ...urlState,
      vin,
      e: engineId,
      d: drivetrainId,
      t: transmissionId,
      go: genericOptions
    }

    if (autoCheckData) {
      stateForVinAndLP = {
        ...stateForVinAndLP,
        'qp/124': { v: autoCheckData?.hasAccidents ? 0 : 1 },
        'qp/83': { v: autoCheckData?.isOriginalOwner ? 0 : 1 },
        'qp/125': { v: autoCheckData?.usedAsRentalCar ? 0 : 1 }
      }
    }

    const stateForYmm = {
      ...urlState,
      vin,
      displayYMM: true
    }

    BrowserUtils.navigateWithUrlState({
      year: { displayName: String(yearId) },
      make: { displayName: make.toLowerCase() },
      model: { displayName: model.toLowerCase() },
      urlState: vin ? stateForVinAndLP : stateForYmm
    })
  })

  useEventBus('VinModalNewOfferClick', () => {
    navigateToNewOfferWithVin()
  })

  useEventBus('DuplicateOfferModalOpenOffer', ({ userId, offerId }) => {
    BrowserUtils.postDuplicateOffer(offerId, userId)
  })

  useEventBus('ToggleFaq', () => {
    const element = document.getElementById('WebComponentContainer-landing')

    if (!element) {
      return
    }

    setTimeout(function () {
      if (BrowserUtils.isIframe()) {
        BrowserUtils.emitComponentSize(element.clientHeight ?? 0, 'landing')
      }
    }, 500)
  })

  useEffect(() => {
    let dynamicEntryWorkflow = makeDefaultDynamicEntryWorkflow()
    if (isCobranding) {
      dynamicEntryWorkflow = experienceState.isCobrandDR ? LandingType.DigitalRetail : LandingType.Assigned
    }

    setDynamicEntryWorkflow(dynamicEntryWorkflow)

    if (experienceState.cobrandingInfo) {
      setPageLoaded(true)
    }
  }, [landingType, experienceState.isCobrandDR])

  const makeDefaultDynamicEntryWorkflow = () => {
    return landingType
  }

  useEffect(() => {
    const cacheBust = Date.now()
    BrowserUtils.addScript(`${environment.funnelWebComponentUrl}?${cacheBust}`, document.head, 'module', false)
    setPageLoaded(!isCobranding)
  }, [])

  useEffect(() => {
    const vehicle = readVehicleData()
    setVehicleData(JSON.stringify(vehicle))

    if (experienceState.cobrandingInfo) {
      setPageLoaded(true)
    }
  }, [location])

  const readVehicleData = () => {
    let vehicle = mergeVehicleDataFromMultipleSources()
    return experienceState.isAdmin ? mergeWithVehicleDataFromProspect(vehicle) : vehicle
  }

  const mergeWithVehicleDataFromProspect = vehicle => {
    const { mileage, trim, trimId } = vehicle
    const prospectVehicle = readVehicleDataFromProspect()
    return { ...vehicle, ...prospectVehicle, mileage, trim, trimId }
  }

  const mergeVehicleDataFromMultipleSources = () => {
    const vehicleQuery = readVehicleDataFromQueryParams()
    const vehiclePath = readVehicleDataFromPathsAndHash()
    const vehicleFetched = readFetchedVehicleData()
    return mergeWith({}, vehicleQuery, vehiclePath, vehicleFetched)
  }

  const readFetchedVehicleData = () => {
    const { vinResults: { vin } = {}, year: { displayName: year } = {}, make: { displayName: make } = {}, model: { displayName: model } = {} } = props
    return { vin, year, make, model }
  }

  const readVehicleDataFromQueryParams = () => {
    const { vin, year, make, model, trim, mileage } = location.initQueryParams
    return { vin, year, make, model, mileage, trim: trim?.replaceAll('+', ' ') }
  }

  const readVehicleDataFromPathsAndHash = () => {
    let [year, make, model] = StringUtils.getPathParameters(location.pathname).params
    let { vin, m: mileage } = StringUtils.getUrlState(location) || {}
    const { inviteid: inviteId } = location.initQueryParams || ''
    make = make && StringUtils.upperCaseFirstLetter(make)
    model = model && StringUtils.upperCaseFirstLetter(model)
    mileage = mileage || props.mileage
    const trim = props.trim?.displayName
    const trims = stripMakeModel(props.trims)

    return { vin, year, make, model, mileage, inviteId, trim, trims }
  }

  const stripMakeModel = trims => trims?.map(({ makeName, modelName, ...trim }) => trim)

  const readVehicleDataFromProspect = () => {
    if (offer?.prospect) {
      const {
        vin,
        trimId,
        vehicleDescription: { year, make, model, trim }
      } = offer.prospect.vehicle
      return { vin, year, make, model, trim, trimId }
    }
  }

  useEffect(() => {
    setModalInfo(
      JSON.stringify({
        vinModalInfo,
        duplicateInfo
      })
    )
  }, [vinModalInfo, duplicateInfo])

  const fireTradeFailed = (reason, year = '', make = '', model = '', trim = '', vin = '') => {
    BrowserUtils.clearLastPostMessage()
    const payload = {
      type: 'External_Trade_Error',
      description: `Vehicle is not eligible${reason ? ' because ' + reason : ''}`,
      year,
      make,
      model,
      trim,
      vin
    }
    BrowserUtils.postMessage(JSON.stringify(payload), '*')
  }

  return (
    <WebComponentContainer pageLoadingComplete={pageLoaded} withLoader={showLoader} maxWidth="100%" location={'landing'}>
      {landingType === LandingType.Unassigned && (
        <ico-landing-unassigned-hydrator
          dynamic-entry-workflow={dynamicEntryWorkflow}
          tab-list={tabListUnAssign}
          installation-mode={DEFAULT_INSTALLATION_MODE}
          is-combined={isCombined}
          show-landing-contact={showLandingContact}
          vehicle={vehicleData}
          show-cityscape={showCityscape}
          category={category}>
          <landing-unassigned></landing-unassigned>
        </ico-landing-unassigned-hydrator>
      )}

      {landingType === LandingType.DigitalRetail && (
        <ico-landing-digital-retail-hydrator
          dynamic-entry-workflow={dynamicEntryWorkflow}
          offer-code={offerCode}
          dealer-id={dealerId}
          installation-mode={DEFAULT_INSTALLATION_MODE}
          vehicle={vehicleData}
          category={category}
          tab-list={tabListAssign}
          dealer-group-id={experienceState.dealerId}
          cobranding-group-id={experienceState.cobrandingInfo?.groupId}>
          <landing-digital-retail></landing-digital-retail>
        </ico-landing-digital-retail-hydrator>
      )}

      {landingType === LandingType.Assigned && (
        <ico-landing-assigned-hydrator
          dynamic-entry-workflow={dynamicEntryWorkflow}
          offer-code={offerCode}
          dealer-id={dealerId}
          installation-mode={DEFAULT_INSTALLATION_MODE}
          show-landing-contact={showLandingContact}
          vehicle={vehicleData}
          is-admin={experienceState.isAdmin}
          category={category}
          tab-list={tabListAssign}
          ymm-disabled={isReviewResubmitOffer || experienceState.isAdmin}
          modal-info={modalInfo}>
          <landing-assigned></landing-assigned>
        </ico-landing-assigned-hydrator>
      )}

      {landingType === LandingType.OEM && (
        <ico-landing-oem-hydrator
          ref={hydratorRef}
          installation-mode={DEFAULT_INSTALLATION_MODE}
          dynamic-entry-workflow={dynamicEntryWorkflow}
          image-url={cobrandingOEMImage}
          dealer-id={dealerId}
          dealer-group-id={experienceState.dealerId}
          cobranding-group-id={experienceState.cobrandingInfo?.groupId}
          offer-code={offerCode}
          vehicle={vehicleData}
          category={category}>
          <landing-oem></landing-oem>
        </ico-landing-oem-hydrator>
      )}
    </WebComponentContainer>
  )
}
